import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { SupportedLanguages } from '@/i18n/i18next';
import { useLanguage } from '@/shared/contexts/LanguageContext';

export const Route = createFileRoute('/_privacy/terms-of-use')({
  component: TermsOfUsePage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      lang: (search.lang as SupportedLanguages | undefined) || undefined,
    };
  },
});

function TermsOfUsePage() {
  const { t } = useTranslation('termsOfUse');
  const { setLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const search = useSearch({ from: Route.id });

  // Change language when query parameter changes
  useEffect(() => {
    if (
      search.lang &&
      (search.lang === 'en' || search.lang === 'he') &&
      search.lang !== language
    ) {
      setLanguage(search.lang);
    }
  }, [search.lang, language, setLanguage]);

  const handleGoBack = () => {
    navigate({ to: '..' });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8 md:px-6 lg:px-8 md:py-12">
        {/* Header */}
        <header className="mb-8 pb-6 border-b border-border text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
            {t('terms_of_use_content.company_name')}
          </h1>
          <h2 className="text-xl md:text-2xl text-muted-foreground mb-2">
            {t('terms_of_use_content.system_name')}
          </h2>
          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {t('terms_of_use')}
          </h3>
          <p className="text-sm md:text-base text-muted-foreground italic">
            {t('last_updated')} - {t('terms_of_use_date')}
          </p>
        </header>

        {/* Main Content */}
        <main className="prose prose-slate max-w-none dark:prose-invert">
          {/* Introduction Section */}
          <section className="mb-8">
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('terms_of_use_content.introduction.welcome')}</p>
            </div>
          </section>

          {/* Terms of Use Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              {t('terms_of_use_content.introduction.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('terms_of_use_content.introduction.terms')}</p>
              <p>{t('terms_of_use_content.introduction.privacy_policy')}</p>
              <p>{t('terms_of_use_content.introduction.important_notice')}</p>
              <p>{t('terms_of_use_content.introduction.updates_notice')}</p>
              <p>{t('terms_of_use_content.introduction.agreement_notice')}</p>
              <p>{t('terms_of_use_content.introduction.contact')}</p>
            </div>
          </section>

          {/* The System Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              1. {t('terms_of_use_content.the_system.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                <strong>1.1</strong>{' '}
                {t('terms_of_use_content.the_system.purpose')}
              </p>
              <p>
                <strong>1.2</strong>{' '}
                {t('terms_of_use_content.the_system.communication_channel')}
              </p>
              <p>
                <strong>1.3</strong>{' '}
                {t('terms_of_use_content.the_system.service_provision')}
              </p>
              <p>
                <strong>1.4</strong>{' '}
                {t('terms_of_use_content.the_system.access_and_use')}
              </p>

              <div className="mt-4">
                <p>
                  <strong>1.5</strong>{' '}
                  {t('terms_of_use_content.the_system.user_accounts.title')}
                </p>
                <div className="ms-6 mt-2 space-y-2">
                  <p>
                    <strong>1.5.1</strong>{' '}
                    {t(
                      'terms_of_use_content.the_system.user_accounts.admin_user'
                    )}
                  </p>
                  <p>
                    <strong>1.5.2</strong>{' '}
                    {t(
                      'terms_of_use_content.the_system.user_accounts.account_management'
                    )}
                  </p>
                </div>
              </div>

              <p>
                <strong>1.6</strong>{' '}
                {t('terms_of_use_content.the_system.updates_and_changes.title')}{' '}
                {t(
                  'terms_of_use_content.the_system.updates_and_changes.content'
                )}
              </p>

              <p>
                <strong>1.7</strong>{' '}
                {t('terms_of_use_content.the_system.system_availability.title')}{' '}
                {t(
                  'terms_of_use_content.the_system.system_availability.content'
                )}
              </p>
            </div>
          </section>

          {/* Terms for Use Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              2. {t('terms_of_use_content.terms_for_use.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                <strong>2.1</strong>{' '}
                {t('terms_of_use_content.terms_for_use.authorized_use')}
              </p>
              <p>
                <strong>2.2</strong>{' '}
                {t('terms_of_use_content.terms_for_use.prohibited_use')}
              </p>
              <p>
                <strong>2.3</strong>{' '}
                {t('terms_of_use_content.terms_for_use.monitoring_rights')}
              </p>
            </div>
          </section>

          {/* Maintenance and Support Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              3. {t('terms_of_use_content.maintenance_and_support.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                <strong>3.1</strong>{' '}
                {t('terms_of_use_content.maintenance_and_support.maintenance')}
              </p>
              <p>
                <strong>3.2</strong>{' '}
                {t(
                  'terms_of_use_content.maintenance_and_support.maintenance_description'
                )}
              </p>
              <p>
                <strong>3.3</strong>{' '}
                {t('terms_of_use_content.maintenance_and_support.support')}
              </p>
            </div>
          </section>

          {/* Intellectual Property Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              4. {t('terms_of_use_content.intellectual_property.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                <strong>4.1</strong>{' '}
                {t('terms_of_use_content.intellectual_property.ownership')}
              </p>
              <p>
                <strong>4.2</strong>{' '}
                {t('terms_of_use_content.intellectual_property.prohibition')}
              </p>
              <p>
                <strong>4.3</strong>{' '}
                {t(
                  'terms_of_use_content.intellectual_property.system_disruption'
                )}
              </p>
            </div>
          </section>

          {/* Company Liability Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              5. {t('terms_of_use_content.company_liability.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                <strong>5.1</strong>{' '}
                {t('terms_of_use_content.company_liability.liability_scope')}
              </p>
            </div>
          </section>

          {/* Terms Update Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              6. {t('terms_of_use_content.terms_update.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                <strong>6.1</strong>{' '}
                {t('terms_of_use_content.terms_update.update_rights')}{' '}
                {t('terms_of_use_content.terms_update.continued_use')}
              </p>
            </div>
          </section>

          {/* Law and Jurisdiction Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              7. {t('terms_of_use_content.law_and_jurisdiction.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                {t('terms_of_use_content.law_and_jurisdiction.applicable_law')}
              </p>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="mt-8 text-center">
          <Button onClick={handleGoBack} variant="default" size="lg">
            {t('go_back')}
          </Button>
        </footer>
      </div>
    </div>
  );
}
